import { useCallback, useEffect, useState } from "react";
import { IconExternalLink, IconInbox, IconPhonePlus, IconPlus } from "@tabler/icons-react";
import { axiosInstance } from "../../utils/axiosInstance";
import PageContainer from "../layouts/PageContainer";
import {
  Card,
  EmptyState,
  ErrorBanner,
  LoadingPanel,
  PageHeader,
  Pill,
  RefreshButton,
  activatable,
} from "../ui";
import HelpdeskWidgets, { SourceTag } from "./HelpdeskWidgets";
import type { HelpdeskTicketsResponse, UnifiedTicket } from "./helpdeskTypes";
import HelpdeskEmailTicketDialog from "./HelpdeskEmailTicketDialog";
import HelpdeskCallLogTicketDialog from "./HelpdeskCallLogTicketDialog";
import HelpdeskTicketDetailDialog from "./HelpdeskTicketDetailDialog";
import HelpdeskCustomerProfileDialog from "./HelpdeskCustomerProfileDialog";

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function hasMacMetadata(row: UnifiedTicket): boolean {
  const m = row.metadata?.machineMacIds;
  return Array.isArray(m) ? m.length > 0 : typeof m === "string" && m.trim() !== "";
}

export default function HelpdeskPage() {
  const [items, setItems] = useState<UnifiedTicket[]>([]);
  const [page] = useState(1);
  const [pageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<UnifiedTicket | null>(null);
  const [customerProfileOpen, setCustomerProfileOpen] = useState(false);
  const [profileCustomerId, setProfileCustomerId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get<HelpdeskTicketsResponse>(
        "/customer-service/helpdesk/tickets",
        { params: { page, pageSize } }
      );
      setItems(res.data.items ?? []);
      setTotal(res.data.total ?? 0);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        "Failed to load tickets.";
      setError(msg);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = (row: UnifiedTicket) => {
    setSelectedTicket(row);
    setDetailOpen(true);
  };

  const openCustomerProfile = (customerId: string) => {
    setDetailOpen(false);
    setSelectedTicket(null);
    setProfileCustomerId(customerId);
    setCustomerProfileOpen(true);
  };

  return (
    <PageContainer>
      <PageHeader
        title="Helpdesk"
        subtitle="Unified tickets from Zammad, Chatwoot and FreePBX when those integrations are configured on the server."
        actions={
          <>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => setEmailDialogOpen(true)}
            >
              <IconPlus size={16} />
              New email ticket
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setCallDialogOpen(true)}
            >
              <IconPhonePlus size={16} />
              Log call
            </button>
            <RefreshButton onClick={() => load()} loading={loading} />
          </>
        }
      />

      {error ? <ErrorBanner onRetry={() => load()}>{error}</ErrorBanner> : null}

      <div className="sq-columns">
        <Card flush title="Tickets" aria-label="Tickets">
          {!error && loading && items.length === 0 ? (
            <LoadingPanel label="Loading tickets" rows={8} />
          ) : null}
          {!error && !loading && items.length === 0 ? (
            <EmptyState icon={<IconInbox size={24} />} title="No tickets to show yet" />
          ) : null}
          {items.length > 0 ? (
            <div className="sq-table-wrap">
              <table className="sq-table is-stacked">
                <thead>
                  <tr>
                    <th scope="col">Ticket</th>
                    <th scope="col">Status</th>
                    <th scope="col">Updated</th>
                    <th scope="col" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr
                      key={`${row.source}-${row.id}`}
                      className="is-clickable"
                      {...activatable(() => openDetail(row))}
                    >
                      <td className="cell-title" title={row.title}>
                        <span className="sq-strong">{row.title}</span>
                        {hasMacMetadata(row) ? (
                          <span className="ms-2">
                            <Pill title="Machine MAC IDs attached">MAC</Pill>
                          </span>
                        ) : null}
                        <div className="helpdesk-ticket-meta">
                          <SourceTag source={row.source} />
                          <span className="sq-muted">{row.customerLabel ?? "—"}</span>
                        </div>
                      </td>
                      <td data-label="Status" className="nowrap">
                        {row.status}
                      </td>
                      <td data-label="Updated" className="nowrap">
                        <span className="sq-muted">{formatWhen(row.updatedAt)}</span>
                      </td>
                      <td className="cell-actions">
                        {row.externalUrl ? (
                          <a
                            href={row.externalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="sq-icon-btn"
                            title="Open in external system"
                            aria-label="Open in external system"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <IconExternalLink size={18} />
                          </a>
                        ) : null}
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDetail(row);
                          }}
                        >
                          Manage
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {!loading && !error && total > 0 ? (
            <div className="sq-pagination">
              <span>
                Showing {items.length} of {total} (page {page})
              </span>
            </div>
          ) : null}
        </Card>

        <HelpdeskWidgets tickets={items} loading={loading} onSelectTicket={openDetail} />
      </div>

      <details className="sq-disclosure helpdesk-about">
        <summary>About integrations</summary>
        <p>
          FreePBX call logs appear when <code>FREEPBX_CDR_*</code> is set on the API. Create email
          and call-log tickets in Zammad; reply on Zammad and Chatwoot; attach machine MAC IDs in
          ticket metadata (requires Postgres helpdesk tables on the API).
        </p>
      </details>

      <HelpdeskEmailTicketDialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        onCreated={() => void load()}
      />
      <HelpdeskCallLogTicketDialog
        open={callDialogOpen}
        onClose={() => setCallDialogOpen(false)}
        onCreated={() => void load()}
      />
      <HelpdeskTicketDetailDialog
        open={detailOpen && selectedTicket != null}
        ticket={selectedTicket}
        onClose={() => {
          setDetailOpen(false);
          setSelectedTicket(null);
        }}
        onSaved={() => void load()}
        onOpenCustomer={openCustomerProfile}
      />
      <HelpdeskCustomerProfileDialog
        open={customerProfileOpen && profileCustomerId != null}
        customerId={profileCustomerId}
        onClose={() => {
          setCustomerProfileOpen(false);
          setProfileCustomerId(null);
        }}
      />
    </PageContainer>
  );
}
